import { PrimeIcons } from '@primevue/core/api';
import axios from 'axios';
import { useConfirm } from 'primevue/useconfirm';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { lowerCase } from '@rygen/rygen-ui/i18n';
import { Loadable, ZonedDateTimeRange } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import { useMileage, UseMileageResponse } from '@/composables/routeBuilder';
import {
  AddShipOrderToShipmentPayload,
  AddStopToShipmentPayload,
  AddToPlanningPoolPayload,
  CreateShipmentFromShipOrdersPayload,
  PlanningStatus,
  ProposedShipmentStop,
  RemoveFromPlanningPoolPayload,
  RemoveShipOrderFromShipmentPayload,
  RemoveStopFromShipmentPayload,
  RoutePlanType,
  ScaffoldRoutePlanPayload,
  SplitShipOrderPayload,
  UpdateAppointmentPayload,
  ValidShipOrder,
} from '@/model/routeBuilder';
import {
  PlanningPool,
  RouteBuilderPhase2Request,
  RoutePlanProposedShipment,
  RoutePlanShipmentRequest,
} from '@/model/routeBuilder/phase2';

import { ArrangeableShipment } from '@/components/multistop/ArrangeableShipmentStopList.vue';

export type RouteBuilderPhase2Step =
  'planning' | 'validation_error' | 'rating' | 'executing';

type PromiseSettledResult<T> =
  PromiseFulfilledResult<T> | PromiseRejectedResult;

const isFulfilled = <T>(
  result: PromiseSettledResult<T>,
): result is PromiseFulfilledResult<T> => result.status === 'fulfilled';

export interface UseRouteBuilderPhase2Response {
  addProposedShipment: (stops: ProposedShipmentStop[]) => void;
  addShipOrderToPlanningPool: (
    payload: AddToPlanningPoolPayload,
  ) => Promise<void>;
  addShipOrderToShipment: (payload: AddShipOrderToShipmentPayload) => void;
  addStopToShipment: (payload: AddStopToShipmentPayload) => void;
  /** `ShipOrder`s the user is adding to the planning pool */
  additionalShipOrders: Ref<Loadable<ValidShipOrder[]>>;
  clearProposedShipments: () => void;
  createShipmentFromShipOrders: (
    payload: CreateShipmentFromShipOrdersPayload,
  ) => void;
  isOnPlanningStep: ComputedRef<boolean>;
  /** True once the plan is committed (EXECUTED); edit actions must be disabled. */
  isReadOnly: ComputedRef<boolean>;
  isScaffoldingRoutePlan: ComputedRef<boolean>;
  /**
   * Load a saved draft (by id) back into the builder. Resolves `true` when the
   * draft could not be opened — a discarded stale payload, or a load error — and
   * the caller should bounce back to the list rather than render a stale builder.
   */
  loadDraftIntoBuilder: (routePlanId: number) => Promise<boolean>;
  loadRelatedShipOrdersForOrders: (
    loadable: Loadable<ValidShipOrder[]>,
    orderIds: number[],
  ) => Promise<void>;
  loadRelatedShipOrdersForShipments: (
    loadable: Loadable<ValidShipOrder[]>,
    shipmentIds: number[],
  ) => Promise<void>;
  mileages: UseMileageResponse['mileages'];
  onScaffoldRoutePlan: (payload: ScaffoldRoutePlanPayload) => void;
  onUpdateAppointment: (payload: UpdateAppointmentPayload) => void;
  planningPool: Ref<PlanningPool>;
  proposedShipments: ComputedRef<RoutePlanProposedShipment[]>;
  rearrangeStops: (shipment: ArrangeableShipment) => void;
  removeShipOrderFromShipment: (
    payload: RemoveShipOrderFromShipmentPayload,
  ) => void;
  removeShipOrdersFromPlanningPool: (
    payload: RemoveFromPlanningPoolPayload,
  ) => void;
  removeShipment: (shipmentIdentifier: number) => void;
  removeStopFromShipment: (payload: RemoveStopFromShipmentPayload) => void;
  request: Ref<RouteBuilderPhase2Request>;
  reset: () => void;
  /** Persist the current proposed shipments as a draft (no shipments created). */
  saveDraft: () => Promise<void>;
  /** True while a saveDraft request is in flight; disables the Save Draft action. */
  isSavingDraft: Ref<boolean>;
  splitShipOrder: (payload: SplitShipOrderPayload) => Promise<void>;
}

export const useRouteBuilderPhase2 = (): UseRouteBuilderPhase2Response => {
  const request: Ref<RouteBuilderPhase2Request> = ref(
    new RouteBuilderPhase2Request(),
  );

  const proposedShipments = computed(() => {
    return request.value.shipmentRequests.map((r) => r.proposedShipment);
  });

  const confirm = useConfirm();
  const { t } = useI18n();
  const { clearMileage, loadMileage, loadMileageForAll, mileages } =
    useMileage(proposedShipments);
  const toast = useToast();

  const _isScaffoldingRoutePlan = ref(false);
  const planningPool: Ref<PlanningPool> = ref(new PlanningPool());
  const additionalShipOrders: Ref<Loadable<ValidShipOrder[]>> = ref({
    loading: false,
  });
  const isSavingDraft = ref(false);

  const isScaffoldingRoutePlan = computed(() => _isScaffoldingRoutePlan.value);

  const isOnPlanningStep = computed(() => {
    return (
      request.value.currentStep === 'planning' ||
      request.value.currentStep === 'validation_error'
    );
  });

  const isReadOnly = computed(() => request.value.isCommitted);

  const loadRelatedShipOrdersForOrders = async (
    loadable: Loadable<ValidShipOrder[]>,
    orderIds: number[],
  ): Promise<void> => {
    try {
      loadable.error = undefined;
      loadable.loading = true;
      // TODO: Batch endpoint for loading related ship orders
      const promises = orderIds.map((orderId) =>
        api.order.findAllRelatedShipOrders(orderId, { onlyUnplanned: true }),
      );
      const resolvedPromises = await Promise.allSettled(promises);
      loadable.data = resolvedPromises
        .filter(isFulfilled)
        .map((result) => result.value)
        .flat();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error loading ship orders', e);
      loadable.error = e;
      toast.addError({
        detail: t('errors.loadingParam', {
          param: lowerCase(t('consolidation.candidates', 2)),
        }),
        error: e,
      });
    } finally {
      loadable.loading = false;
    }
  };

  const loadRelatedShipOrdersForShipments = async (
    loadable: Loadable<ValidShipOrder[]>,
    shipmentIds: number[],
  ): Promise<void> => {
    try {
      loadable.error = undefined;
      loadable.loading = true;
      // TODO: Batch endpoint for loading related ship orders
      const promises = shipmentIds.map((shipmentId) =>
        api.shipment.findAllRelatedShipOrders(shipmentId),
      );
      const resolvedPromises = await Promise.allSettled(promises);
      loadable.data = resolvedPromises
        .filter(isFulfilled)
        .map((result) => result.value)
        .flat();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error loading ship orders', e);
      loadable.error = e;
      toast.addError({
        detail: t('errors.loadingParam', {
          param: lowerCase(t('consolidation.candidates', 2)),
        }),
        error: e,
      });
    } finally {
      loadable.loading = false;
    }
  };

  const addProposedShipment = (stops: ProposedShipmentStop[]) => {
    const { identifier } = request.value.addProposedShipment(stops);
    loadMileage(identifier);
  };

  const clearProposedShipments = () => {
    planningPool.value.shipOrders.forEach((so) => {
      so.origin.planningStatus = PlanningStatus.UNPLANNED;
      so.destination.planningStatus = PlanningStatus.UNPLANNED;
    });
    request.value.clearProposedShipments();
    mileages.value = {};
  };

  const addShipOrderToPlanningPool = async ({
    orderIds,
  }: AddToPlanningPoolPayload) => {
    await loadRelatedShipOrdersForOrders(additionalShipOrders.value, orderIds);
    const newShipOrders = additionalShipOrders.value.data ?? [];
    planningPool.value.addShipOrders(newShipOrders);
  };

  const addShipOrderToShipment = ({
    shipOrder,
    shipmentIdentifier,
  }: AddShipOrderToShipmentPayload) => {
    const { proposedShipment } =
      request.value.findProposedShipment(shipmentIdentifier);

    const hasMatchingOrigin =
      proposedShipment.hasOriginStop(shipOrder) ||
      proposedShipment.origin?.isPlaceholderStop;
    const hasMatchingDestination =
      proposedShipment.hasDestinationStop(shipOrder) ||
      proposedShipment.destination?.isPlaceholderStop;

    if (hasMatchingOrigin && hasMatchingDestination) {
      onAddToShipmentConfirm(shipOrder, proposedShipment);
      return;
    }

    const stopType = hasMatchingOrigin
      ? t('orders.destination')
      : t('orders.origin');
    confirm.require({
      message: t('routeBuilder.leaveShipOrderUnresolved.message', {
        stopType,
        shipmentIdentifier: proposedShipment.shipmentIdentifier,
      }),
      icon: `${PrimeIcons.EXCLAMATION_TRIANGLE} text-warning`,
      group: 'rygen-confirm-dialog',
      header: t('routeBuilder.leaveShipOrderUnresolved.header'),
      accept: () => addPartialShipOrderToShipment(shipOrder, proposedShipment),
      acceptProps: {
        class: 'p-button-sm',
        label: t('routeBuilder.leaveShipOrderUnresolved.leaveUnresolved'),
        severity: 'warn',
      },
      reject: () => onAddToShipmentConfirm(shipOrder, proposedShipment),
      rejectProps: {
        class: 'p-button-sm p-button-text',
        label: t('common.addParam', { param: stopType }),
      },
    });
  };

  const addPartialShipOrderToShipment = (
    shipOrder: ValidShipOrder,
    proposedShipment: RoutePlanProposedShipment,
  ) => {
    proposedShipment.addPartialShipOrder(shipOrder);
  };

  const onAddToShipmentConfirm = (
    shipOrder: ValidShipOrder,
    proposedShipment: RoutePlanProposedShipment,
  ) => {
    proposedShipment.addShipOrder(shipOrder);

    loadMileage(proposedShipment.shipmentIdentifier);
  };

  const createShipmentFromShipOrders = (
    payload: CreateShipmentFromShipOrdersPayload,
  ) => {
    onScaffoldRoutePlan({
      routePlanType: RoutePlanType.CONSOLIDATE,
      shipOrderIds: payload.shipOrders.map((so) => so.id),
    });
  };

  const removeShipOrderFromShipmentConfirm = (
    shipOrder: ValidShipOrder,
    shipmentIdentifier: number,
    skipMileage = false,
  ) => {
    const { proposedShipment } =
      request.value.findProposedShipment(shipmentIdentifier);

    const isOriginOnShipment = proposedShipment.hasOriginStop(shipOrder);
    const isDestinationOnShipment =
      proposedShipment.hasDestinationStop(shipOrder);
    proposedShipment.removeShipOrder(shipOrder);

    // Update removed shipOrder's planning status
    const planningPoolShipOrder = planningPool.value.findShipOrder(
      shipOrder.id,
    );

    if (isOriginOnShipment) {
      planningPoolShipOrder.origin.planningStatus = PlanningStatus.UNPLANNED;
    }

    if (isDestinationOnShipment) {
      planningPoolShipOrder.destination.planningStatus =
        PlanningStatus.UNPLANNED;
    }

    if (!skipMileage) {
      loadMileage(shipmentIdentifier);
    }
  };

  const removeShipOrderFromShipment = ({
    shipOrder,
    shipmentIdentifiers,
    onConfirm,
  }: RemoveShipOrderFromShipmentPayload) => {
    // Backstop for read-only plans: the line-items modal's remove reaches here
    // without going through a button we disable for readonly.
    if (isReadOnly.value) {
      return;
    }
    confirm.require({
      message: t('routeBuilder.removeShipOrderConfirmation'),
      icon: `${PrimeIcons.EXCLAMATION_TRIANGLE} text-danger`,
      group: 'rygen-confirm-dialog',
      header: t('routeBuilder.leaveShipOrderUnresolved.header'),
      accept: () => {
        shipmentIdentifiers.forEach((identifier) =>
          removeShipOrderFromShipmentConfirm(shipOrder, identifier),
        );
        onConfirm?.();
      },
      acceptProps: {
        class: 'p-button-sm',
        label: t('common.removeParam', { param: t('shipOrders.shipOrders') }),
        icon: PrimeIcons.MINUS_CIRCLE,
        severity: 'danger',
      },
      rejectProps: {
        class: 'p-button-sm p-button-text',
        label: t('common.cancel'),
      },
    });
  };

  const removeStopFromShipment = ({
    stop,
    shipmentIdentifier,
    numShipOrdersOnStop,
  }: RemoveStopFromShipmentPayload) => {
    if (!numShipOrdersOnStop) {
      onRemoveStopFromShipmentConfirm({ stop, shipmentIdentifier });
      return;
    }

    confirm.require({
      group: 'rygen-confirm-dialog',
      message: t('routeBuilder.removeStopConfirmation', {
        count: numShipOrdersOnStop,
      }),
      header: t('common.confirmRemove'),
      accept: () =>
        onRemoveStopFromShipmentConfirm({ stop, shipmentIdentifier }),
      acceptProps: {
        class: 'p-button-sm',
        label: t('common.removeParam', {
          param: t('common.paramNum', {
            param: t('shipments.stops'),
            num: stop.sequenceNumber,
          }),
        }),
        icon: PrimeIcons.MINUS_CIRCLE,
        severity: 'danger',
      },
      rejectProps: {
        class: 'p-button-sm p-button-text',
        label: t('common.cancel'),
      },
    });
  };

  const onRemoveStopFromShipmentConfirm = ({
    stop,
    shipmentIdentifier,
  }: Pick<RemoveStopFromShipmentPayload, 'stop' | 'shipmentIdentifier'>) => {
    const { proposedShipment } =
      request.value.findProposedShipment(shipmentIdentifier);

    // hold onto shipping point ids on this stop so we can update their status
    // if they're removed
    const shipOrderShippingPointIds = stop.shipOrderShippingPointIds;

    const removedStop = proposedShipment.removeStop(stop.sequenceNumber);

    if (removedStop) {
      planningPool.value.shipOrders
        .filter(
          (so) =>
            shipOrderShippingPointIds.includes(so.origin.id) ||
            shipOrderShippingPointIds.includes(so.destination.id),
        )
        .forEach((so) => {
          const shippingPoint = shipOrderShippingPointIds.includes(so.origin.id)
            ? so.origin
            : so.destination;
          shippingPoint.planningStatus = PlanningStatus.UNPLANNED;
        });

      removedStop.isPlaceholderStop
        ? clearMileage(shipmentIdentifier)
        : loadMileage(shipmentIdentifier);
    }
  };

  const addStopToShipment = (payload: AddStopToShipmentPayload) => {
    const { proposedShipment } = request.value.findProposedShipment(
      payload.shipmentIdentifier,
    );

    if (
      payload.stopSelector === 'origin' ||
      payload.stopSelector === 'destination'
    ) {
      const stop = proposedShipment[payload.stopSelector];
      if (stop) {
        stop.location = payload.location;
      }
    } else if (payload.sequenceNumber) {
      const stop = proposedShipment.stops.find(
        (s) => s.sequenceNumber === payload.sequenceNumber,
      );
      if (stop) {
        stop.location = payload.location;
      }
    } else {
      proposedShipment.addStop(payload);
    }

    loadMileage(proposedShipment.identifier);
  };

  const rearrangeStops = (shipment: ArrangeableShipment) => {
    const isProposedShipment = shipment instanceof RoutePlanProposedShipment;
    if (!isProposedShipment) {
      console.error(
        '`shipment` should be a RoutePlanProposedShipment',
        shipment,
      );
      throw new Error('`shipment` should be a RoutePlanProposedShipment');
    }

    const { proposedShipment } = request.value.findProposedShipment(
      shipment.identifier,
    );
    proposedShipment.stops = shipment.stops;
    proposedShipment.transportSegments = shipment.transportSegments;
    loadMileage(proposedShipment.identifier);
  };

  const removeShipment = (shipmentIdentifier: number) => {
    const { proposedShipment } =
      request.value.findProposedShipment(shipmentIdentifier);

    proposedShipment.shipOrderIds
      .map((soId) => planningPool.value.findShipOrder(soId))
      .forEach((so) => {
        removeShipOrderFromShipmentConfirm(so, shipmentIdentifier, true);
      });

    request.value.removeProposedShipment(shipmentIdentifier);
    mileages.value[shipmentIdentifier] = {
      response: { loading: false },
    };
  };

  const removeShipOrdersFromPlanningPool = (
    payload: RemoveFromPlanningPoolPayload,
  ) => {
    const shipOrderIds = payload.shipOrders.map((so) => so.id).filter(notNull);
    planningPool.value.removeShipOrders(shipOrderIds);
  };

  const splitShipOrder = async (payload: SplitShipOrderPayload) => {
    try {
      planningPool.value.loading = true;

      const response = await api.shipOrder.splitBeta(payload);
      planningPool.value.insertNewShipOrdersAfterExistingShipOrder(
        response.originalShipOrder,
        ...response.newShipOrders,
      );
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error splitting ship order', e);
      toast.addError({
        detail: t('shipOrders.shipOrderSplit.errors.splitting'),
        error: e,
      });
    } finally {
      planningPool.value.loading = false;
    }
  };

  const onScaffoldRoutePlan = async (payload: ScaffoldRoutePlanPayload) => {
    try {
      _isScaffoldingRoutePlan.value = true;
      const newShipments = await api.routePlan.scaffoldRoutePlan(payload);

      newShipments.forEach((newShipment) => {
        // Backend transport segments arrive with only a shipOrderId. Attach the full ship order
        // the planning pool already holds so line items and the ship-order list render.
        newShipment.transportSegments.forEach((ts) => {
          ts.shipOrder = planningPool.value.findShipOrder(ts.shipOrderId);
        });
        // Backend shipments don't flip the pool's planning status, so mark every shipping
        // point that landed on a stop as planned (per-shipping-point keeps splits partial).
        const plannedShippingPointIds = new Set(
          newShipment.stops.flatMap((stop) => stop.shipOrderShippingPointIds),
        );
        newShipment.transportSegments.forEach((ts) => {
          ts.shipOrder?.stops.forEach((shippingPoint) => {
            if (plannedShippingPointIds.has(shippingPoint.id)) {
              shippingPoint.planningStatus = PlanningStatus.PLANNED;
            }
          });
        });
        request.value.insertProposedShipment(newShipment);
        loadMileage(newShipment.identifier);
      });
    } catch (e) {
      toast.addError({
        detail: t('routeBuilder.errorBuildingScaffold'),
        error: e,
      });
    } finally {
      _isScaffoldingRoutePlan.value = false;
    }
  };

  const onUpdateAppointment = ({
    shipmentIdentifier,
    stopSequenceNumber,
    appointment,
  }: UpdateAppointmentPayload) => {
    // Backstop: the picker is hidden in readonly, but guard the mutation too.
    if (isReadOnly.value) {
      return;
    }
    const { proposedShipment } =
      request.value.findProposedShipment(shipmentIdentifier);
    const stop = proposedShipment.stops.find(
      (s) => s.sequenceNumber === stopSequenceNumber,
    );

    if (!stop) {
      console.error(
        `Could not find stop ${stopSequenceNumber} for shipment ${shipmentIdentifier}`,
      );
      throw new Error('Could not find stop to update');
    }

    stop.requestedAppointmentRange = new ZonedDateTimeRange({
      start: appointment.start,
      end: appointment.end,
    });
    stop.location.timeZone = appointment.timeZone;
  };

  const saveDraft = async () => {
    // Guard against a double-click racing two creates: the first hasn't stored
    // the returned id yet, so a second call would POST routePlanId=undefined and
    // create a duplicate draft.
    if (isSavingDraft.value) {
      return;
    }
    isSavingDraft.value = true;
    try {
      planningPool.value.loading = true;
      const routePlanId = await api.routePlan.saveDraft(
        request.value.routePlanId,
        request.value.proposedShipments,
      );
      request.value.routePlanId = routePlanId;
      toast.addSuccess({ detail: t('routeBuilder.draftSaved') });
    } catch (e) {
      console.error('Error saving route plan draft', e);
      toast.addError({
        detail: t('errors.savingParam', { param: t('routeBuilder.routePlan') }),
        error: e,
      });
    } finally {
      planningPool.value.loading = false;
      isSavingDraft.value = false;
    }
  };

  // A saved draft's transport segments only carry ship-order ids. Re-fetch those
  // ship orders, mark them planned, add them to the pool (so they show under
  // "Selected Shiporders"), and attach each to its segment so line items resolve.
  // Ship orders that no longer load (deleted, out of scope, or now invalid) are
  // skipped so the draft still opens — but we warn the user so a partially loaded
  // plan isn't mistaken for a complete one.
  const rehydrateDraftShipOrders = async (
    proposedShipments: RoutePlanProposedShipment[],
  ) => {
    const segments = proposedShipments.flatMap((ps) => ps.transportSegments);
    const unresolvedIds = [
      ...new Set(
        segments
          .filter((s) => !s.shipOrder && s._shipOrderId != null)
          .map((s) => s._shipOrderId as number),
      ),
    ];
    if (!unresolvedIds.length) {
      return;
    }

    const shipOrders = (
      await Promise.all(
        unresolvedIds.map(async (id) => {
          try {
            const shipOrder = new ValidShipOrder(
              await api.shipOrder.findById(id),
            );
            shipOrder.origin.planningStatus = PlanningStatus.PLANNED;
            shipOrder.destination.planningStatus = PlanningStatus.PLANNED;
            return shipOrder;
          } catch (e) {
            console.warn(`Skipping ship order ${id} on the reopened draft`, e);
            return null;
          }
        }),
      )
    ).filter(notNull);
    if (shipOrders.length < unresolvedIds.length) {
      toast.addWarning({ detail: t('routeBuilder.shipOrdersNotLoaded') });
    }
    if (!shipOrders.length) {
      return;
    }

    planningPool.value.addShipOrders(shipOrders);

    const byId = new Map(shipOrders.map((so) => [so.id, so]));
    segments.forEach((s) => {
      const shipOrder =
        s._shipOrderId != null ? byId.get(s._shipOrderId) : undefined;
      if (shipOrder) {
        s.shipOrder = shipOrder;
      }
    });
  };

  const loadDraftIntoBuilder = async (
    routePlanId: number,
  ): Promise<boolean> => {
    try {
      planningPool.value.loading = true;
      // loadForBuilder returns status + live shipmentCount alongside the shipments, so a single
      // request carries everything the builder needs (no separate summary fetch).
      // A stale draft (older payload format) is rejected by the endpoint; the catch below
      // bounces the user back to the list, same as any other load failure.
      const view = await api.routePlan.loadForBuilder(routePlanId);

      request.value = new RouteBuilderPhase2Request({
        routePlanId,
        status: view.status,
        // Committed shipments already exist → builder opens read-only (see isCommitted).
        shipmentCount: view.shipmentCount,
        shipmentRequests: view.proposedShipments.map(
          (proposedShipment) =>
            new RoutePlanShipmentRequest({ proposedShipment }),
        ),
      });

      await rehydrateDraftShipOrders(request.value.proposedShipments);

      const identifiers = request.value.shipmentRequests.map(
        (s) => s.proposedShipment.identifier,
      );
      loadMileageForAll(identifiers);
    } catch (e) {
      console.error('Error loading route plan into builder', e);
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('routeBuilder.routePlan'),
        }),
        error: e,
      });
      // Bounce to the list (like a discarded draft) rather than leave the user in a
      // half-built builder with a stale request the caller would then act on.
      return true;
    } finally {
      planningPool.value.loading = false;
    }
    return false;
  };

  const reset = () => {
    planningPool.value.reset();
    additionalShipOrders.value = { loading: false };
    request.value.reset();
  };

  return {
    addProposedShipment,
    addShipOrderToPlanningPool,
    addShipOrderToShipment,
    addStopToShipment,
    additionalShipOrders,
    clearProposedShipments,
    createShipmentFromShipOrders,
    isOnPlanningStep,
    isReadOnly,
    isScaffoldingRoutePlan,
    loadDraftIntoBuilder,
    loadRelatedShipOrdersForOrders,
    loadRelatedShipOrdersForShipments,
    mileages,
    onScaffoldRoutePlan,
    onUpdateAppointment,
    planningPool,
    proposedShipments,
    rearrangeStops,
    removeShipOrderFromShipment,
    removeShipOrdersFromPlanningPool,
    removeShipment,
    removeStopFromShipment,
    request,
    reset,
    saveDraft,
    isSavingDraft,
    splitShipOrder,
  };
};
